import React, { useState } from "react";
import { conditionLibrary, bodySystems } from "@/lib/claimData";
import { submitConditionSuggestion } from "@/lib/api";
import { VIEWS } from "@/lib/navConfig";
import { MagnifyingGlass, Plus, Tag, PencilLine, CheckCircle, ArrowRight, Info } from "@phosphor-icons/react";

export default function ConditionsView({ addCondition, issues = [], setActiveView }) {
  const [query, setQuery] = useState("");
  const [filterSystem, setFilterSystem] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customCode, setCustomCode] = useState("");
  const [customSystem, setCustomSystem] = useState("musculoskeletal");
  const [recentAdds, setRecentAdds] = useState([]);
  const [submitNote, setSubmitNote] = useState("");
  const [suggestSent, setSuggestSent] = useState(false);

  function flashAdded(code) {
    setRecentAdds((prev) => [...prev, code]);
    setTimeout(() => setRecentAdds((prev) => prev.filter((c) => c !== code)), 2200);
  }

  function handleAddCondition(c) {
    addCondition(c);
    flashAdded(c.code);
  }

  async function handleAddCustom() {
    if (!customName.trim()) return;
    const code = customCode.trim() || "4.20";
    // 1. Add to current workspace
    addCondition({
      code,
      name: customName.trim(),
      system: customSystem,
      symptoms: [],
      r: [],
      common: false,
    });
    // 2. Submit upstream for the next library update (best-effort, non-blocking)
    const res = await submitConditionSuggestion({
      name: customName.trim(),
      code: customCode.trim(),
      system: customSystem,
      notes: submitNote.trim(),
    });
    flashAdded(code);
    setCustomName("");
    setCustomCode("");
    setSubmitNote("");
    if (res) {
      setSuggestSent(true);
      setTimeout(() => setSuggestSent(false), 3500);
    }
    setShowCustom(false);
  }

  const claimedCodes = new Set(issues.map((i) => i.code));

  const filtered = conditionLibrary
    .filter((c) => {
      const haystack = `${c.name} ${c.code} ${c.symptoms.join(" ")} ${(c.aliases || []).join(" ")}`.toLowerCase();
      const matchesQuery = !query || haystack.includes(query.toLowerCase());
      const matchesSystem = !filterSystem || c.system === filterSystem;
      return matchesQuery && matchesSystem;
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="view-grid" data-testid="conditions-view">
      <div className="panel" data-testid="conditions-search-panel">
        <div className="panel-header">
          <MagnifyingGlass size={18} weight="bold" />
          <span>CONDITION LIBRARY</span>
          <span className="header-count mono">{conditionLibrary.length}</span>
        </div>
        <div className="panel-body">
          <p className="panel-desc">Search ratable VA conditions by name, diagnostic code, or symptoms. {conditionLibrary.length} conditions across {bodySystems.length} body systems with 38 CFR rating percentages.</p>
          <div className="search-row" data-testid="search-row">
            <div className="search-box">
              <MagnifyingGlass size={16} weight="bold" className="search-icon" />
              <input
                className="search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="PTSD, migraine, tinnitus, back, knee..."
                data-testid="condition-search-input"
              />
            </div>
            <select
              className="field-input system-filter"
              value={filterSystem}
              onChange={(e) => setFilterSystem(e.target.value)}
              data-testid="system-filter"
            >
              <option value="">All Systems</option>
              {bodySystems.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div className="btn-row" style={{ marginTop: 12, alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <button className="btn btn--outline" onClick={() => setShowCustom(!showCustom)} data-testid="toggle-custom-btn">
              <PencilLine size={14} weight="bold" /> {showCustom ? "CANCEL" : "ADD CUSTOM CONDITION"}
            </button>
            {issues.length > 0 && setActiveView && (
              <button
                className="btn btn--ghost"
                onClick={() => setActiveView(VIEWS.tracker)}
                data-testid="go-issues-tracker-btn"
                style={{ marginLeft: "auto" }}
              >
                VIEW IN CLAIM TRACKER ({issues.length}) <ArrowRight size={14} weight="bold" />
              </button>
            )}
          </div>
          {showCustom && (
            <div className="custom-condition-form" data-testid="custom-condition-form">
              <div className="field-grid" style={{ marginTop: 12 }}>
                <label className="field">
                  <span className="field-label">CONDITION NAME</span>
                  <input className="field-input" value={customName} onChange={(e) => setCustomName(e.target.value)} placeholder="e.g. Insomnia, Chronic pain syndrome..." data-testid="custom-name" />
                </label>
                <label className="field">
                  <span className="field-label">DIAGNOSTIC CODE (optional)</span>
                  <input className="field-input mono" value={customCode} onChange={(e) => setCustomCode(e.target.value)} placeholder="e.g. 4.20 (unlisted)" data-testid="custom-code" />
                </label>
                <label className="field">
                  <span className="field-label">BODY SYSTEM</span>
                  <select className="field-input" value={customSystem} onChange={(e) => setCustomSystem(e.target.value)} data-testid="custom-system">
                    {bodySystems.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </label>
                <label className="field" style={{ gridColumn: "1 / -1" }}>
                  <span className="field-label">NOTES FOR REVIEWERS (OPTIONAL)</span>
                  <textarea
                    className="field-input"
                    rows={2}
                    value={submitNote}
                    onChange={(e) => setSubmitNote(e.target.value)}
                    placeholder="Anything that helps us pick the right DC for the next update - synonyms, CFR section, source, etc."
                    data-testid="custom-notes"
                  />
                </label>
                <div className="field" style={{ display: "flex", alignItems: "flex-end" }}>
                  <button className="btn btn--accent" onClick={handleAddCustom} disabled={!customName.trim()} data-testid="add-custom-btn">
                    <Plus size={14} weight="bold" /> ADD TO CLAIM + SUBMIT FOR NEXT UPDATE
                  </button>
                </div>
              </div>
              <p className="panel-desc" style={{ marginTop: 8, marginBottom: 0 }}>
                Conditions not in the 38 CFR schedule can be rated by analogy under DC 4.20. Submitting a custom condition also queues it for review so we can add the proper DC to the official library in the next update.
              </p>
            </div>
          )}
          {suggestSent && (
            <div
              data-testid="suggest-toast"
              style={{
                marginTop: 12, background: "#F0FDF4", border: "1px solid #BBF7D0",
                color: "#14532D", padding: "10px 14px", borderRadius: 4, fontSize: 13,
                display: "flex", alignItems: "center", gap: 8,
              }}
            >
              <CheckCircle size={14} weight="fill" />
              Thanks! Your condition was submitted to the team for inclusion in the next library update.
            </div>
          )}
          {/* Library coverage disclaimer */}
          <div
            data-testid="library-disclaimer"
            style={{
              marginTop: 14, background: "var(--branch-accent-soft, rgba(31,58,107,0.08))", border: "1px solid var(--branch-accent, #1F3A6B)",
              borderRadius: 6, padding: "10px 14px", color: "var(--text-primary)", fontSize: 13,
              display: "flex", alignItems: "flex-start", gap: 10,
            }}
          >
            <Info size={16} weight="fill" style={{ color: "var(--branch-accent, #1F3A6B)", flexShrink: 0, marginTop: 1 }} />
            <div>
              <strong>Don't see your condition?</strong> The library currently covers {conditionLibrary.length}+ entries from 38 CFR Part 4 plus common aliases.
              If your condition isn't listed, use <strong>ADD CUSTOM CONDITION</strong> above - we'll claim it under DC 4.20 (rated by analogy) in your workspace immediately
              and add it to our review queue for inclusion in the next library update.
            </div>
          </div>
        </div>
      </div>

      <div className="conditions-grid" data-testid="conditions-grid">
        {filtered.length < conditionLibrary.length && (
          <div className="panel" style={{ gridColumn: "1 / -1", padding: "8px 16px" }}>
            <span className="mono" style={{ fontSize: 12, color: "var(--text-secondary)" }}>Showing {filtered.length} of {conditionLibrary.length} conditions</span>
          </div>
        )}
        {filtered.map((c) => {
          const justAdded = recentAdds.includes(c.code);
          const alreadyClaimed = claimedCodes.has(c.code);
          return (
            <div key={`${c.code}-${c.name}`} className={`condition-card${justAdded ? " condition-card--added" : ""}`} data-testid={`condition-${c.code}`}>
              <div className="condition-code mono" data-testid={`code-${c.code}`}>DC {c.code}</div>
              <div className="condition-name">{c.name}</div>
              {c.tag === "secondary-only" && (
                <div data-testid={`tag-secondary-${c.code}`} style={{ display: "inline-block", marginTop: 4, padding: "2px 8px", background: "#FEF3C7", color: "#7C4A03", border: "1px solid #FCD34D", borderRadius: 3, fontSize: 10, fontWeight: 800, letterSpacing: "0.06em" }}>
                  SECONDARY ONLY
                </div>
              )}
              {c.tag === "not-ratable" && (
                <div data-testid={`tag-notratable-${c.code}`} style={{ display: "inline-block", marginTop: 4, padding: "2px 8px", background: "#FEE2E2", color: "#7F1D1D", border: "1px solid #FCA5A5", borderRadius: 3, fontSize: 10, fontWeight: 800, letterSpacing: "0.06em" }} title="Generally not service-connectable per 38 CFR 3.303(c)">
                  NOT SERVICE-CONNECTABLE
                </div>
              )}
              {c.tag === "not-ratable-alone" && (
                <div data-testid={`tag-notalone-${c.code}`} style={{ display: "inline-block", marginTop: 4, padding: "2px 8px", background: "#E0E7FF", color: "#3730A3", border: "1px solid #A5B4FC", borderRadius: 3, fontSize: 10, fontWeight: 800, letterSpacing: "0.06em" }} title="Usually claimed as part of a broader diagnosis or after progression">
                  USUALLY NOT RATED ALONE
                </div>
              )}
              {c.tag === "alias" && (
                <div data-testid={`tag-alias-${c.code}`} style={{ display: "inline-block", marginTop: 4, padding: "2px 8px", background: "#F1F5F9", color: "#475569", border: "1px solid #CBD5E1", borderRadius: 3, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em" }}>
                  COMMON ALIAS
                </div>
              )}
              <div className="condition-system">
                <Tag size={12} weight="bold" />
                {bodySystems.find((s) => s.id === c.system)?.label || c.system}
              </div>
              <div className="condition-symptoms">
                {c.symptoms.slice(0, 3).map((s) => (
                  <span key={s} className="symptom-tag">{s}</span>
                ))}
              </div>
              {c.r && c.r.length > 0 && (
                <div className="bz-ratings" style={{ marginTop: 2 }}>
                  <span className="bz-ratings-label">RATINGS:</span>
                  {c.r.map((r) => <span key={r} className="bz-rating-pill mono">{r}%</span>)}
                </div>
              )}
              <button
                className={`btn btn--sm condition-add-btn ${justAdded ? "btn--success" : alreadyClaimed ? "btn--outline" : "btn--accent"}`}
                onClick={() => handleAddCondition(c)}
                data-testid={`add-condition-${c.code}`}
              >
                {justAdded ? (
                  <><CheckCircle size={14} weight="fill" /> ADDED</>
                ) : alreadyClaimed ? (
                  <><Plus size={14} weight="bold" /> ADD ANOTHER</>
                ) : (
                  <><Plus size={14} weight="bold" /> ADD TO CLAIM</>
                )}
              </button>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="empty-state" data-testid="no-conditions-found">
            <MagnifyingGlass size={32} weight="duotone" />
            <p>No conditions match your search.</p>
          </div>
        )}
      </div>
    </div>
  );
}
